import { makeAutoObservable } from 'mobx';

import { resetAllStore } from '../../shared/stores/resetAllStore';
import { FilterStore } from '../../shared/util-stores';
import {
  ParticipantDivision,
  ParticipantEditInfo,
  ParticipantRowInfo,
} from '../interfaces/participants-list/ParticipantRowInfo';
import {
  ParticipantListFilters,
  ParticipantListFormValues,
  ParticipantListSortField,
} from '../interfaces/participants-list/ParticipantListFilters';
import { participantService } from '../services/participantService';

class ParticipantsListStore {
  participantsList: ParticipantRowInfo[] = [];
  divisions: ParticipantDivision[] = [];
  filterMethods: FilterStore<ParticipantListFormValues, ParticipantListSortField>;
  private challengeId: number | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.filterMethods = new FilterStore<
      ParticipantListFormValues,
      ParticipantListSortField
    >(this.handleGetParticipantsList);
  }

  setDefaultValues() {
    this.participantsList = [];
    this.divisions = [];
    this.challengeId = null;
    this.filterMethods.setDefaultValues();
  }

  private async handleGetParticipantsList({
    requestedPage,
    sort,
  }: ParticipantListFilters) {
    if (!this.challengeId) {
      throw new Error('challengeId is required');
    }

    const data = await participantService.getParticipantsList(this.challengeId, {
      requestedPage,
      sort,
    });
    const { content } = data;

    this.setParticipantsList(content);

    return data;
  }

  async initParticipantsTab(challengeId: number) {
    this.setChallengeId(challengeId);

    await Promise.all([this.getParticipantsList(), this.getDivisions()]);
  }

  async getParticipantsList() {
    return this.filterMethods.getData();
  }

  private async getDivisions() {
    if (!this.challengeId) {
      throw new Error('challengeId is required');
    }

    const { divisions } = await participantService.getDivisions(this.challengeId);
    this.setDivisions(divisions || []);
  }

  async deleteParticipant(participantId: number) {
    if (!this.challengeId) {
      throw new Error('challengeId is required');
    }

    await participantService.deleteParticipant(this.challengeId, participantId);
    await this.getParticipantsList();
  }

  async updateParticipant(participantId: number, data: ParticipantEditInfo) {
    if (!this.challengeId) {
      throw new Error('challengeId is required');
    }

    await participantService.updateParticipant(this.challengeId, participantId, data);
    await this.getParticipantsList();
  }

  private setChallengeId(challengeId: number) {
    this.challengeId = challengeId;
  }

  private setParticipantsList(participantsList: ParticipantRowInfo[]) {
    this.participantsList = participantsList;
  }

  private setDivisions(divisions: ParticipantDivision[]) {
    this.divisions = divisions;
  }
}

const participantsListStore = new ParticipantsListStore();

resetAllStore.addResetMethod(participantsListStore.setDefaultValues);

export { participantsListStore, ParticipantsListStore };
